import {
	CheckOutlined,
	DownOutlined,
	IdcardOutlined,
	LogoutOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import { useGetIdentity, useLogout } from "@refinedev/core";
import {
	App,
	Avatar,
	Button,
	Descriptions,
	Divider,
	Dropdown,
	Form,
	Input,
	type MenuProps,
	Modal,
	Space,
	Tag,
	Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AuthUser, hasSuperAdminScope } from "../api/auth-provider.js";
import { http, crestHttp } from "../api/client.js";
import type { CrmMode } from "../api/crm-mode-config.js";
import { getAvailableCrmModes } from "../api/crm-mode-preference.js";
import { useCrmMode } from "../api/use-crm-mode.js";
import { tx } from "../i18n/translate.js";
import type { CrestThemeName } from "../theme/antd-theme.js";
import {
	setStoredCrestTheme,
	useCrestTheme,
} from "../theme/crest-theme-preference.js";
import { avatarInitials } from "../utils/avatar-initials.js";
import { crmRoleNameI18nKey } from "../utils/crm-role-name.js";

function getModeLabel(mode: CrmMode): string {
	return mode === "overseas"
		? tx("hardcoded:components.roleavatarswitcher.16f0f25a")
		: tx("hardcoded:components.roleavatarswitcher.39f48843");
}

interface CrmProfile {
	businessOrgs: Array<{ businessOrgId: string; name: string }>;
	roles: Array<{ roleId: string; name: string; roleType: string }>;
}

interface AgentProfile {
	roles: Array<{ key: string; name: string }>;
	defaulted: boolean;
}

function isCrmWebProject(): boolean {
	if (typeof __CREST_WEB_PROJECT__ !== "undefined")
		return __CREST_WEB_PROJECT__ === "crm-web";
	if (typeof window === "undefined") return false;
	return (
		window.location.pathname.startsWith("/app/crm") ||
		window.location.pathname.startsWith("/crm")
	);
}

/**
 * 始终渲染用户头像。
 * 单国内/单海外用户展示当前属性；双身份用户通过顶部独立入口切换。
 */
export function RoleAvatarSwitcher() {
	const { i18n, t: translateCrmRole } = useTranslation("crmUserAuthorization");
	const { t: translateCommon } = useTranslation();
	const { data: user } = useGetIdentity<AuthUser>();
	const { mutate: logout } = useLogout();
	const { message } = App.useApp();
	const [form] = Form.useForm<PasswordFormValues>();
	const [profileOpen, setProfileOpen] = useState(false);
	const [crmProfile, setCrmProfile] = useState<CrmProfile | null>(null);
	const [crmProfileLoading, setCrmProfileLoading] = useState(false);
	const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
	const [agentProfileLoading, setAgentProfileLoading] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [savingPassword, setSavingPassword] = useState(false);

	const availableModes = useMemo(() => getAvailableCrmModes(user), [user]);
	const hasBothModes = availableModes.length >= 2;
	const currentMode = useCrmMode();
	const useCrmProfile = isCrmWebProject();
	const hasAllAppAccess = hasSuperAdminScope(user);
	const showCrmRoles =
		useCrmProfile || hasAllAppAccess || (user?.appNames ?? []).includes("crm");
	const showAgentRoles =
		hasAllAppAccess || (user?.appNames ?? []).includes("agent");

	useEffect(() => {
		if (!profileOpen || !showCrmRoles) return;
		let cancelled = false;
		setCrmProfileLoading(true);
		http
			.get<CrmProfile>("/crm/profile/me")
			.then((res) => {
				if (!cancelled) setCrmProfile(res.data);
			})
			.catch(() => {
				if (!cancelled) setCrmProfile(null);
			})
			.finally(() => {
				if (!cancelled) setCrmProfileLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [profileOpen, showCrmRoles]);

	useEffect(() => {
		if (!profileOpen || !showAgentRoles) return;
		let cancelled = false;
		setAgentProfileLoading(true);
		http
			.get<AgentProfile>("/personal-agent/profile/me")
			.then((res) => {
				if (!cancelled) setAgentProfile(res.data);
			})
			.catch(() => {
				if (!cancelled) setAgentProfile(null);
			})
			.finally(() => {
				if (!cancelled) setAgentProfileLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [profileOpen, showAgentRoles]);

	const displayBusinessUnits = useMemo(() => {
		if (useCrmProfile && crmProfile) {
			return crmProfile.businessOrgs.map((org) => ({
				id: org.businessOrgId,
				name: org.name,
			}));
		}
		if (useCrmProfile) return [];
		const names = user?.businessUnitName
			? [
					{
						id: user.businessUnitId ?? user.businessUnitName,
						name: user.businessUnitName,
					},
				]
			: [];
		return names;
	}, [crmProfile, useCrmProfile, user?.businessUnitId, user?.businessUnitName]);

	const applicationRoles = useMemo(() => {
		return [
			{
				key: "platform",
				label: translateCommon("userAvatar.platformRoles", "Platform 角色"),
				roles: user?.platformRoleNames ?? user?.roleNames ?? [],
				loading: false,
			},
			...(showCrmRoles
				? [
						{
							key: "crm",
							label: translateCommon("userAvatar.crmRoles", "CRM 角色"),
							roles: crmProfile?.roles.map((role) => role.name) ?? [],
							loading: crmProfileLoading,
						},
					]
				: []),
			...(showAgentRoles
				? [
						{
							key: "agent",
							label: translateCommon("userAvatar.agentRoles", "Agent 角色"),
							roles: agentProfile?.roles.map((role) => role.name) ?? [],
							loading: agentProfileLoading,
						},
					]
				: []),
		];
	}, [
		agentProfile,
		agentProfileLoading,
		crmProfile,
		crmProfileLoading,
		showAgentRoles,
		showCrmRoles,
		translateCommon,
		user?.platformRoleNames,
		user?.roleNames,
	]);

	const localizedRoleName = (roleName: string) => {
		const key = crmRoleNameI18nKey(roleName);
		return key ? translateCrmRole(key) : roleName;
	};

	const items = useMemo<MenuProps["items"]>(() => {
		return [
			{
				key: "profile",
				icon: <IdcardOutlined />,
				label: tx("hardcoded:components.roleavatarswitcher.9d8c7e31"),
				onClick: () => setProfileOpen(true),
			},
			{
				key: "settings",
				icon: <SettingOutlined />,
				label: tx("hardcoded:components.roleavatarswitcher.a7f4c2d0"),
				onClick: () => setSettingsOpen(true),
			},
			{ type: "divider" },
			...(!hasBothModes
				? [
						{
							key: "mode-group",
							type: "group" as const,
							label: tx("hardcoded:components.roleavatarswitcher.61b2d2d4"),
							children: [
								{
									key: currentMode,
									disabled: true,
									icon: <CheckOutlined />,
									label: getModeLabel(currentMode),
								},
							],
						},
						{ type: "divider" as const },
					]
				: []),
			{
				key: "logout",
				icon: <LogoutOutlined />,
				label: tx("header:logout"),
				onClick: () => logout(),
			},
		];
	}, [currentMode, hasBothModes, logout]);

	async function changePassword(values: PasswordFormValues) {
		setSavingPassword(true);
		try {
			await crestHttp.post("/auth/change-password", {
				oldPassword: values.oldPassword,
				newPassword: values.newPassword,
			});
			form.resetFields();
			message.success(tx("hardcoded:components.roleavatarswitcher.4f30b6b7"));
		} catch (err: unknown) {
			const e = err as { response?: { data?: { message?: string } } };
			message.error(
				e.response?.data?.message ??
					tx("hardcoded:components.roleavatarswitcher.f020f65a"),
			);
		} finally {
			setSavingPassword(false);
		}
	}

	const initials = useMemo(
		() => avatarInitials(user?.fullName),
		[user?.fullName],
	);
	const displayName = user?.fullName?.trim() ?? "";
	const uiTheme = useCrestTheme();
	const themeOptions: Array<{
		value: CrestThemeName;
		label: string;
		note?: string;
	}> = [
		{
			value: "sky",
			label: translateCommon("userSettings.themeSky", "天蓝"),
			note: translateCommon("userSettings.themeDefault", "默认"),
		},
		{
			value: "orange",
			label: translateCommon("userSettings.themeOrange", "Baicells 橙"),
		},
	];

	return (
		<>
			<Dropdown menu={{ items }} placement="bottomRight" trigger={["click"]}>
				<button
					type="button"
					className="crest-user-chip"
					aria-label={translateCommon("userAvatar.menu", "{{name}} 账号菜单", {
						name: displayName || initials,
					})}
				>
					<Avatar
						size={28}
						className="crest-user-chip-avatar"
						style={{ fontSize: initials.length > 1 ? 12 : 13 }}
					>
						{initials}
					</Avatar>
					{displayName ? (
						<span className="crest-user-chip-name">{displayName}</span>
					) : null}
					<DownOutlined aria-hidden className="crest-user-chip-caret" />
				</button>
			</Dropdown>
			<Modal
				title={tx("hardcoded:components.roleavatarswitcher.9d8c7e31")}
				open={profileOpen}
				onCancel={() => setProfileOpen(false)}
				footer={null}
				width={520}
			>
				<Descriptions column={1} size="small" bordered>
					<Descriptions.Item
						label={tx("hardcoded:components.roleavatarswitcher.5ab42f91")}
					>
						{user?.fullName ?? "-"}
					</Descriptions.Item>
					<Descriptions.Item
						label={tx("hardcoded:components.roleavatarswitcher.5c7ed106")}
					>
						{user?.domainName ?? "-"}
					</Descriptions.Item>
					<Descriptions.Item
						label={tx("hardcoded:components.roleavatarswitcher.91dbb767")}
					>
						<Space size={[4, 4]} wrap>
							{crmProfileLoading
								? "Loading..."
								: displayBusinessUnits.length > 0
									? displayBusinessUnits.map((bu) => (
											<Tag key={bu.id}>{bu.name}</Tag>
										))
									: "-"}
						</Space>
					</Descriptions.Item>
					{applicationRoles.map((group) => (
						<Descriptions.Item key={group.key} label={group.label}>
							<Space size={[4, 4]} wrap>
								{group.loading
									? "Loading..."
									: group.roles.length > 0
										? group.roles.map((role) => (
												<Tag key={role}>
													{group.key === "crm" ? localizedRoleName(role) : role}
												</Tag>
											))
										: "-"}
							</Space>
						</Descriptions.Item>
					))}
				</Descriptions>
			</Modal>
			<Modal
				title={tx("hardcoded:components.roleavatarswitcher.a7f4c2d0")}
				open={settingsOpen}
				onCancel={() => setSettingsOpen(false)}
				footer={null}
				width={520}
			>
				<Space direction="vertical" style={{ width: "100%" }} size={12}>
					<Typography.Title level={5} style={{ margin: 0 }}>
						{translateCommon("userSettings.theme", "界面主题")}
					</Typography.Title>
					<div
						className="crest-theme-options"
						role="radiogroup"
						aria-label={translateCommon("userSettings.theme", "界面主题")}
					>
						{themeOptions.map((option) => (
							<label
								key={option.value}
								className="crest-theme-option"
								data-theme-option={option.value}
							>
								<input
									type="radio"
									name="crest-ui-theme"
									value={option.value}
									checked={uiTheme === option.value}
									onChange={() => setStoredCrestTheme(option.value)}
								/>
								<span
									className="crest-theme-option-swatch"
									aria-hidden="true"
								/>
								<span className="crest-theme-option-label">{option.label}</span>
								{option.note ? (
									<span className="crest-theme-option-note">{option.note}</span>
								) : null}
							</label>
						))}
					</div>
					<Typography.Text type="secondary">
						{translateCommon(
							"userSettings.themeHint",
							"仅保存在当前浏览器，切换后立即生效。",
						)}
					</Typography.Text>
					<Divider style={{ margin: "4px 0" }} />
					<Descriptions column={1} size="small" bordered>
						<Descriptions.Item
							label={tx("hardcoded:components.roleavatarswitcher.08d325f3")}
						>
							<Tag color={currentMode === "overseas" ? "blue" : "green"}>
								{getModeLabel(currentMode)}
							</Tag>
						</Descriptions.Item>
						<Descriptions.Item
							label={tx("hardcoded:components.roleavatarswitcher.6d2ea9a6")}
						>
							{i18n.language?.toUpperCase() ?? "-"}
						</Descriptions.Item>
						<Descriptions.Item
							label={tx("hardcoded:components.roleavatarswitcher.7d77d073")}
						>
							{user?.defaultCurrencySymbol ?? ""} {user?.defaultCurrency ?? "-"}
						</Descriptions.Item>
					</Descriptions>
					<Typography.Text type="secondary">
						{tx("hardcoded:components.roleavatarswitcher.8cbf162d")}
					</Typography.Text>
					<Divider style={{ margin: "4px 0" }} />
					<Typography.Title level={5} style={{ margin: 0 }}>
						{tx("hardcoded:components.roleavatarswitcher.4c1b0b4e")}
					</Typography.Title>
					<Form
						form={form}
						layout="vertical"
						onFinish={changePassword}
						requiredMark={false}
					>
						<Form.Item
							label={tx("hardcoded:components.roleavatarswitcher.e0a98aa3")}
							name="oldPassword"
							rules={[
								{
									required: true,
									message: tx(
										"hardcoded:components.roleavatarswitcher.88e84432",
									),
								},
							]}
						>
							<Input.Password autoComplete="current-password" />
						</Form.Item>
						<Form.Item
							label={tx("hardcoded:components.roleavatarswitcher.7f6173b9")}
							name="newPassword"
							rules={[
								{
									required: true,
									message: tx(
										"hardcoded:components.roleavatarswitcher.75f2cba9",
									),
								},
								{
									min: 8,
									message: tx(
										"hardcoded:components.roleavatarswitcher.5aa12dc8",
									),
								},
							]}
							hasFeedback
						>
							<Input.Password autoComplete="new-password" />
						</Form.Item>
						<Form.Item
							label={tx("hardcoded:components.roleavatarswitcher.c53254c8")}
							name="confirmPassword"
							dependencies={["newPassword"]}
							rules={[
								{
									required: true,
									message: tx(
										"hardcoded:components.roleavatarswitcher.c53cec6e",
									),
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue("newPassword") === value)
											return Promise.resolve();
										return Promise.reject(
											new Error(
												tx("hardcoded:components.roleavatarswitcher.fab8c53c"),
											),
										);
									},
								}),
							]}
							hasFeedback
						>
							<Input.Password autoComplete="new-password" />
						</Form.Item>
						<Form.Item style={{ marginBottom: 0 }}>
							<Button type="primary" htmlType="submit" loading={savingPassword}>
								{tx("hardcoded:components.roleavatarswitcher.b62cb8b4")}
							</Button>
						</Form.Item>
					</Form>
				</Space>
			</Modal>
		</>
	);
}

type PasswordFormValues = {
	oldPassword: string;
	newPassword: string;
	confirmPassword: string;
};
